import { message } from '@/components/Message';
import { useBaseInfo } from '@/context/BaseInfoContext';
import { DateShortFormat } from '@/models/common';
import { UserFullInfo } from '@/models/user';
import { getUserAvatar } from '@/utils/uiHelper';
import { ClockCircleOutlined, InfoCircleOutlined, LineOutlined, PhoneOutlined, RiseOutlined } from '@ant-design/icons';
import { Grid, Skeleton, Space, Typography } from 'antd';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React from 'react';
import { BasicInfoOperator } from '../../pages/User/BasicInfoOperator';
import SensitiveContent from '../SensitiveContent';
import UserGender from '../User/Gender';
import UserName from '../User/Name';
import UserRelationBar from '../User/UserRelationBar';

const useStyles = createStyles(({ css, cx, token, isDarkMode }) => {
  const container = cx(css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px 20px 10px;
    background: ${token.colorBgContainer};
    width: 100%;
  `);
  const identity = cx(css`
    font-size: 1.2em;
    display: flex;
    align-items: center;
  `);
  const identityInner = cx(css`
    font-size: 1.2em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
  `);
  const identityActions = cx(css`
    display: flex;
    gap: 8px;
  `);
  const username = cx(css`
    display: flex;
    flex-wrap: wrap;
    font-weight: ${token.fontWeightStrong};
    font-size: ${token.fontSizeHeading5}px;
    align-items: center;
  `);

  const communityChannel = cx(css`
    display: flex;
    @media (max-width: ${token.screenXSMax}px) {
      justify-content: space-between;
    }
  `);
  const memo = cx(css`
    /* margin-top: 0.3em; */
  `);
  return { container, username, identity, identityInner, identityActions, memo, communityChannel };
});

export type Props = {
  info: UserFullInfo;
  onReload: () => void;
  loading: boolean;
};

export const UserBasicInfo: React.FC<Props> = ({ info, onReload, loading }: Props) => {
  const [, , helpers] = useBaseInfo();
  const { xs } = Grid.useBreakpoint();
  const { styles, cx } = useStyles();
  if (!info) {
    return <Skeleton avatar active paragraph={{ rows: 2 }} />;
  }

  const { user } = info;

  return (
    <div className={cx(styles.container)}>
      <div className={styles.identity}>
        <div
          onDoubleClick={() => {
            if (user.fromUserId && user.fromUserId > 0) {
              helpers.showUserModal({
                userId: user.fromUserId,
              });
            } else {
              message.info('该客户无上游客户');
            }
          }}>
          {getUserAvatar(user, { size: 45, style: { marginRight: 5 } })}
        </div>
        <div className={styles.identityInner}>
          <div className={styles.username}>
            <UserGender style={{ marginRight: 3 }} gender={user.gender} />
            <UserName user={user} />
          </div>
          <div className={styles.identityActions}>
            <BasicInfoOperator user={user} onSuccess={onReload} />
          </div>
        </div>
      </div>
      <Typography.Text type={'secondary'}>
        <Space direction={xs ? 'vertical' : 'horizontal'} size={3} split={xs ? null : <LineOutlined rotate={90} />}>
          <Space direction="horizontal" size={3} split={<LineOutlined rotate={90} />}>
            <span>
              <ClockCircleOutlined /> Since {dayjs(user.addTime).format(DateShortFormat)}
            </span>
            <span>
              <RiseOutlined /> {info.uas}
            </span>
          </Space>
          {Boolean(user.mobile || info.lso) ? (
            <Space direction="horizontal" size={3} split={<LineOutlined rotate={90} />}>
              {user.mobile ? (
                <Typography.Text type={'secondary'}>
                  <PhoneOutlined /> {user.mobile}
                </Typography.Text>
              ) : null}
              {info.lso ? <span>上次护理: {dayjs(info.lso.endTime).fromNow()}</span> : null}
            </Space>
          ) : null}
        </Space>
      </Typography.Text>

      <UserRelationBar user={user} spaceProps={{ size: xs ? 0 : 15 }} className={styles.communityChannel} />
      {user.memo ? (
        <>
          <SensitiveContent
            className={styles.memo}
            content={user.memo}
            tip={
              <>
                <InfoCircleOutlined /> 客户备注
              </>
            }
          />
        </>
      ) : null}
    </div>
  );
};
